import React,{useRef,useState,useCallback,useEffect} from 'react'

/**
 * ImageCropModal — canvas-based circular crop tool.
 * Displays a circular mask over the image; user drags/scales to position.
 * Returns a cropped circular PNG data-URL on confirm.
 */
export default function ImageCropModal({file,onConfirm,onCancel}) {
    const canvasRef=useRef(null)
    const [img,setImg]=useState(null)
    const [offset,setOffset]=useState({x:0,y:0})
    const [scale,setScale]=useState(1)
    const dragging=useRef(false)
    const lastPos=useRef(null)
    const SIZE=280 // canvas size px

    // Load image
    useEffect(()=>{
        if(!file) return
        const url=URL.createObjectURL(file)
        const image=new Image()
        image.onload=()=>{
            setImg(image)
            // Center image initially
            const s=Math.max(SIZE/image.width,SIZE/image.height)
            setScale(s)
            setOffset({x:(SIZE-image.width*s)/2,y:(SIZE-image.height*s)/2})
        }
        image.src=url
        return ()=>URL.revokeObjectURL(url)
    },[file])

    // Draw canvas
    useEffect(()=>{
        const canvas=canvasRef.current
        if(!canvas||!img) return
        const ctx=canvas.getContext('2d')
        ctx.clearRect(0,0,SIZE,SIZE)

        // Draw image
        ctx.save()
        ctx.drawImage(img,offset.x,offset.y,img.width*scale,img.height*scale)
        ctx.restore()

        // Dark overlay outside circle
        ctx.save()
        ctx.fillStyle='rgba(0,0,0,0.45)'
        ctx.fillRect(0,0,SIZE,SIZE)
        ctx.globalCompositeOperation='destination-out'
        ctx.beginPath()
        ctx.arc(SIZE/2,SIZE/2,SIZE/2-4,0,Math.PI*2)
        ctx.fill()
        ctx.restore()

        // Circle border
        ctx.save()
        ctx.strokeStyle='rgba(255,255,255,0.8)'
        ctx.lineWidth=2
        ctx.beginPath()
        ctx.arc(SIZE/2,SIZE/2,SIZE/2-4,0,Math.PI*2)
        ctx.stroke()
        ctx.restore()
    },[img,offset,scale])

    const onMouseDown=useCallback((e)=>{
        dragging.current=true
        lastPos.current={x:e.clientX,y:e.clientY}
    },[])

    const onMouseMove=useCallback((e)=>{
        if(!dragging.current) return
        const dx=e.clientX-lastPos.current.x
        const dy=e.clientY-lastPos.current.y
        lastPos.current={x:e.clientX,y:e.clientY}
        setOffset(prev=>({x:prev.x+dx,y:prev.y+dy}))
    },[])

    const onMouseUp=useCallback(()=>{dragging.current=false},[])

    // Touch support
    const onTouchStart=useCallback((e)=>{
        dragging.current=true
        lastPos.current={x:e.touches[0].clientX,y:e.touches[0].clientY}
    },[])
    const onTouchMove=useCallback((e)=>{
        if(!dragging.current) return
        const dx=e.touches[0].clientX-lastPos.current.x
        const dy=e.touches[0].clientY-lastPos.current.y
        lastPos.current={x:e.touches[0].clientX,y:e.touches[0].clientY}
        setOffset(prev=>({x:prev.x+dx,y:prev.y+dy}))
    },[])

    const handleConfirm=useCallback(()=>{
        const out=document.createElement('canvas')
        out.width=SIZE
        out.height=SIZE
        const ctx=out.getContext('2d')
        // Clip circle
        ctx.beginPath()
        ctx.arc(SIZE/2,SIZE/2,SIZE/2,0,Math.PI*2)
        ctx.clip()
        ctx.drawImage(img,offset.x,offset.y,img.width*scale,img.height*scale)
        onConfirm(out.toDataURL('image/png'))
    },[img,offset,scale,onConfirm])

    if(!file) return null

    return (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/60' role="dialog" aria-modal="true" aria-label="Crop profile photo">
            <div className='bg-white rounded-2xl shadow-2xl p-6 flex flex-col gap-4 items-center max-w-sm w-full mx-4'>
                <h2 className='text-lg font-bold text-dark'>Crop your photo</h2>
                <p className='text-xs text-gray-500'>Drag to position, use the slider to zoom</p>

                {/* Canvas */}
                <canvas
                    ref={canvasRef}
                    width={SIZE}
                    height={SIZE}
                    className='rounded-full overflow-hidden cursor-grab active:cursor-grabbing border border-gray-200'
                    style={{width:SIZE,height:SIZE}}
                    onMouseDown={onMouseDown}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    onMouseLeave={onMouseUp}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onMouseUp}
                />

                {/* Zoom slider */}
                <div className='flex items-center gap-3 w-full'>
                    <span className='text-xs text-gray-400'>−</span>
                    <input
                        type='range'
                        min={0.5}
                        max={3}
                        step={0.01}
                        value={scale}
                        onChange={e=>setScale(parseFloat(e.target.value))}
                        className='flex-1 accent-primary'
                        aria-label='Zoom level'
                    />
                    <span className='text-xs text-gray-400'>+</span>
                </div>

                {/* Actions */}
                <div className='flex gap-3 w-full'>
                    <button onClick={onCancel} className='flex-1 py-2 text-sm font-semibold rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors'>Cancel</button>
                    <button onClick={handleConfirm} className='flex-1 py-2 text-sm font-semibold text-white rounded-lg bg-primary hover:bg-primary-600 transition-colors'>Apply</button>
                </div>
            </div>
        </div>
    )
}
